// 文件名: src/components/Td2000g3Bg3.js
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowRight, Maximize, HelpCircle, RefreshCw, Target } from 'lucide-react';
import defaultConfig from '../config/td2000g3_bg3_params.json';
import { useTranslation } from '../i18n';
import { useUnit } from '../contexts/UnitContext';
import { formatLength, getDisplayValue, getMetersFromInput, getUnitLabel, getAreaUnitLabel } from '../utils/unitConversion';

const Tooltip = ({ explanation }) => (
  <div className="relative flex items-center group">
    <HelpCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs p-2 text-sm text-white bg-gray-800 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
      {explanation}
    </div>
  </div>
);

const ParameterInput = ({ label, value, unit, onChange, tooltip, isEditable }) => {
  const finalIsEditable = !!onChange && isEditable;
  const inputClasses = finalIsEditable ? "bg-white" : "bg-gray-100 cursor-not-allowed";
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 flex items-center gap-1">
        {label} {tooltip && <Tooltip explanation={tooltip} />}
      </label>
      <div className="mt-1 flex rounded-md shadow-sm">
        <input
          type="number"
          value={value}
          onChange={onChange}
          className={`block w-full flex-1 rounded-none rounded-l-md border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm ${inputClasses} py-2 px-3`}
          readOnly={!finalIsEditable}
        />
        {unit && <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">{unit}</span>}
      </div>
    </div>
  );
};


export const LENS_PRESETS = {
    "2mm": {
        label: "2mm"
    },
    "8mm": {
        label: "8mm"
    }
};

const PARAMS_STORAGE_KEY = 'td2000g3_bg3-admin-params';

const degToRad = (degrees) => degrees * Math.PI / 180;

const mmult = (matrix, vector) => [
    matrix[0][0] * vector[0] + matrix[0][1] * vector[1] + matrix[0][2] * vector[2],
    matrix[1][0] * vector[0] + matrix[1][1] * vector[1] + matrix[1][2] * vector[2],
    matrix[2][0] * vector[0] + matrix[2][1] * vector[1] + matrix[2][2] * vector[2],
];

export const calculateTd2000g3Bg3Coverage = (inputs, params, t) => {
    const { installationHeight, xTilt, yTilt, lensType } = inputs;
    const { personHeight, detectionResolutionWidth, shoulderTargetPixel, magnificationPersonHeightRef, waterDropBaseHeight } = params;

    let rawHalfWidth;
    let rawHalfHeightRef;

    if (lensType === "8mm") {
        rawHalfWidth = 0.3483;
        rawHalfHeightRef = 0.3884;
    } else { // Default to 2mm
        rawHalfWidth = 2.13;
        rawHalfHeightRef = 1.88;
    }
    
    const shoulderWidth = 0.4;
    const autoMagnificationFactor = Math.max(1, shoulderTargetPixel / (shoulderWidth / (installationHeight - magnificationPersonHeightRef) / (rawHalfWidth * 2) * detectionResolutionWidth));
    
    const xTiltRad = degToRad(xTilt);
    const yTiltRad = degToRad(yTilt);
    
    const waterdropHeight = 540 - Math.min(540, waterDropBaseHeight * autoMagnificationFactor);
    const halfHeight1m = rawHalfHeightRef / waterDropBaseHeight * (540 - waterdropHeight) / 2;
    
    const pVectors = [
        [rawHalfWidth / autoMagnificationFactor, halfHeight1m / autoMagnificationFactor, 1],
        [rawHalfWidth / autoMagnificationFactor, -halfHeight1m / autoMagnificationFactor, 1],
        [-rawHalfWidth / autoMagnificationFactor, -halfHeight1m / autoMagnificationFactor, 1],
        [-rawHalfWidth / autoMagnificationFactor, halfHeight1m / autoMagnificationFactor, 1]
    ];
    
    const shoulderDistance = installationHeight - personHeight;
    if (shoulderDistance <= 0) return { error: t('error_heightTooLow') };
    
    const R = [
        [Math.cos(yTiltRad), Math.sin(yTiltRad) * Math.sin(xTiltRad), Math.sin(yTiltRad) * Math.cos(xTiltRad)],
        [0, Math.cos(xTiltRad), -Math.sin(xTiltRad)],
        [-Math.sin(yTiltRad), Math.cos(yTiltRad) * Math.sin(xTiltRad), Math.cos(yTiltRad) * Math.cos(xTiltRad)]
    ];
    
    const rpVectors = pVectors.map(p => mmult(R, p));

    const visionField = rpVectors.map(rp => {
        if (rp[2] <= 0) return { x: Infinity, y: Infinity };
        const t_ = installationHeight / rp[2];
        return { x: rp[0] * t_, y: rp[1] * t_ };
    }).filter(p => p.x !== Infinity);

    const rawCountingField = rpVectors.map(rp => {
        if (rp[2] <= 0) return { x: Infinity, y: Infinity, z_comp: 0 };
        const t_ = shoulderDistance / rp[2];
        return { x: rp[0] * t_, y: rp[1] * t_, z_comp: rp[2] };
    }).filter(p => p.x !== Infinity);

    if (visionField.length < 4 || rawCountingField.length < 4) {
        return { error: t('error_cannotImageToGround') }
    }
    
    const minShoulderDist = Math.sqrt((pVectors[0][0]**2 + pVectors[0][1]**2) * shoulderDistance**2 + shoulderDistance**2);
    const dMax2_h2 = minShoulderDist**2 - shoulderDistance**2;
    
    const maxCountingField = rawCountingField.map(rcf => {
        const t_prime = (dMax2_h2 > 0 && (rcf.x**2 + rcf.y**2) > 0) ? Math.sqrt(dMax2_h2 / (rcf.x**2 + rcf.y**2)) : 0;
        const t_shoulder = rcf.z_comp > 0 ? shoulderDistance / rcf.z_comp : 0;
        return {
            x: Math.sign(t_shoulder) * t_prime * rcf.x,
            y: Math.sign(t_shoulder) * t_prime * rcf.y,
            t_prime,
            t_shoulder,
        };
    });
    
    const coverageField = maxCountingField.map((mcf, i) => {
        const useRaw = mcf.t_prime > 1 && mcf.t_shoulder > 0;
        const x = useRaw ? rawCountingField[i].x : mcf.x;
        const y = useRaw ? rawCountingField[i].y : mcf.y;
        return { x, y };
    });

    const calculateMetrics = (field) => {
        const p1 = field[0], p2 = field[1], p3 = field[2], p4 = field[3];
        const width1 = Math.sqrt((p1.x - p4.x)**2 + (p1.y - p4.y)**2);
        const width2 = Math.sqrt((p2.x - p3.x)**2 + (p2.y - p3.y)**2);
        const depth1 = Math.sqrt((p1.x - p2.x)**2 + (p1.y - p2.y)**2);
        const depth2 = Math.sqrt((p4.x - p3.x)**2 + (p4.y - p3.y)**2);
        const avgWidth = (width1 + width2) / 2;
        const avgHeight = (depth1 + depth2) / 2;
        return { avgWidth, avgHeight };
    };

    let { avgWidth, avgHeight } = calculateMetrics(coverageField);
    if (lensType === "8mm") {
        if (installationHeight >= 13.0 && installationHeight <= 14.4) {
            avgHeight = Math.min(avgHeight, 4.4);
        } else if (installationHeight >= 14.5 && installationHeight <= 18.0) {
            avgHeight = Math.min(avgHeight, 4.5);
        }
    }
    const visionMetrics = calculateMetrics(visionField);
    
    const p1_area = coverageField[0], p2_area = coverageField[1], p3_area = coverageField[2], p4_area = coverageField[3];
    const area = Math.abs(p1_area.x*p2_area.y - p1_area.y*p2_area.x + p2_area.x*p3_area.y - p2_area.y*p3_area.x + p3_area.x*p4_area.y - p3_area.y*p4_area.x + p4_area.x*p1_area.y - p4_area.y*p1_area.x) / 2;

    return {
        error: null,
        summary: {
            coverageField, visionField,
            coverageAvgWidth: avgWidth, coverageAvgHeight: avgHeight,
            visionAvgWidth: visionMetrics.avgWidth, visionAvgHeight: visionMetrics.avgHeight,
            area,
        },
        intermediate: { autoMagnificationFactor, shoulderDistance, rawHalfWidth, halfHeight1m }
    };
};

const Controls = ({ inputs, setInputs, isAdmin }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    const handleInputChange = (key, value, isFloat = false) => {
        setInputs(prev => ({ ...prev, [key]: isFloat ? parseFloat(value) : parseInt(value, 10) }));
    };
    
    const handleLensChange = (e) => {
        const newLensType = e.target.value;
        setInputs(prev => ({
            ...prev,
            lensType: newLensType,
        }));
    };
    
    const tiltLimit = isAdmin ? 45 : 10;
    
    const heightRanges = {
        "2mm": { min: 2.2, max: 8.0 },
        "8mm": { min: 5.0, max: 18.0 }
    };

    const currentHeightRange = heightRanges[inputs.lensType] || heightRanges["2mm"];
    
    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('controlsTitle')}</h2>
            <div className="space-y-6">
                <div>
                    <label htmlFor="lensType" className="block text-sm font-medium text-gray-600 mb-1">{t('lensModel')}</label>
                    <select id="lensType" value={inputs.lensType} onChange={handleLensChange}
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500">
                        {Object.keys(LENS_PRESETS).map(key => <option key={key} value={key}>{LENS_PRESETS[key].label}</option>)}
                    </select>
                    {inputs.lensType === '8mm' && (
                        <p className="text-red-600 text-sm mt-1">
                            TD2000BG3 does not currently support the 8mm lens.
                        </p>
                    )}
                </div>
                <div>
                    <label htmlFor="installationHeight" className="block text-sm font-medium text-gray-600 mb-1">
                        {t('installationHeight')}: <span className="font-bold text-cyan-600">{formatLength(inputs.installationHeight, unitSystem, 1)}</span>
                         <span className="text-xs ml-2 text-gray-500">({t('range')}: {formatLength(currentHeightRange.min, unitSystem, 1)} - {formatLength(currentHeightRange.max, unitSystem, 1)})</span>
                    </label>
                    <input type="range" id="installationHeight" min={getDisplayValue(currentHeightRange.min, unitSystem)} max={getDisplayValue(currentHeightRange.max, unitSystem)} step={unitSystem === 'imperial' ? "0.5" : "0.1"} value={getDisplayValue(inputs.installationHeight, unitSystem)}
                        onChange={(e) => handleInputChange('installationHeight', getMetersFromInput(e.target.value, unitSystem), true)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                    {inputs.lensType === '2mm' && inputs.installationHeight > 6 && (
                        <p className="text-red-600 text-sm mt-1">
                            TD2000BG3 does not support this height.
                        </p>
                    )}
                </div>
                <div>
                    <label htmlFor="xTilt" className="block text-sm font-medium text-gray-600 mb-1">
                        {t('xTilt')}: <span className="font-bold text-cyan-600">{inputs.xTilt}°</span>
                    </label>
                    <input type="range" id="xTilt" min={-tiltLimit} max={tiltLimit} step="1" value={inputs.xTilt}
                        onChange={(e) => handleInputChange('xTilt', e.target.value)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
                <div>
                    <label htmlFor="yTilt" className="block text-sm font-medium text-gray-600 mb-1">
                        {t('yTilt')}: <span className="font-bold text-cyan-600">{inputs.yTilt}°</span>
                    </label>
                    <input type="range" id="yTilt" min={-tiltLimit} max={tiltLimit} step="1" value={inputs.yTilt}
                        onChange={(e) => handleInputChange('yTilt', e.target.value)} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
            </div>
        </div>
    );
};

const SummaryResults = ({ data }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!data || !data.summary) return null;
    const { coverageAvgWidth, coverageAvgHeight, area } = data.summary;
    
    const ResultCard = ({ title, value, unit, icon }) => (
        <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-4">
            {icon}
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-xl font-bold text-gray-800">{value} <span className="text-sm font-normal">{unit}</span></p>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('coverageSummaryTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ResultCard title={t('averageWidth')} value={getDisplayValue(coverageAvgWidth, unitSystem).toFixed(1)} unit={getUnitLabel(unitSystem)} icon={<Maximize className="w-8 h-8 text-blue-500" />} />
                <ResultCard title={t('averageDepth')} value={getDisplayValue(coverageAvgHeight, unitSystem).toFixed(1)} unit={getUnitLabel(unitSystem)} icon={<ArrowRight className="w-8 h-8 text-red-500 transform rotate-90" />} />
                <ResultCard title={t('coverageArea')} value={getDisplayValue(area, unitSystem).toFixed(1)} unit={getAreaUnitLabel(unitSystem)} icon={<Target className="w-8 h-8 text-purple-500" />} />
            </div>
        </div>
    );
};

const SideViewDiagram = ({ inputs, results }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!results || !results.summary) return null;

    const { installationHeight, yTilt } = inputs;
    const { coverageField, visionField } = results.summary;
    
    const width = 500, height = 350, padding = 40;

    const yCoordsCoverage = coverageField.map(p => p.y);
    const yCoordsVision = visionField.map(p => p.y);
    
    const minY = Math.min(...yCoordsCoverage);
    const maxY = Math.max(...yCoordsCoverage);
    const visionMinY = Math.min(...yCoordsVision);
    const visionMaxY = Math.max(...yCoordsVision);
    
    const maxAbsY = Math.max(Math.abs(visionMinY), Math.abs(visionMaxY));
    const horizontalSpan = maxAbsY * 2;

    const scaleX = horizontalSpan > 0 ? (width - padding * 2) / horizontalSpan : 1;
    const scaleY = installationHeight > 0 ? (height - padding * 2.5) / installationHeight : 1;
    const scale = Math.min(scaleX, scaleY);
    
    const groundY = height - padding * 2;
    const camX = width / 2;
    const camY = groundY - installationHeight * scale;

    const visionStartPx = camX + visionMinY * scale;
    const visionEndPx = camX + visionMaxY * scale;

    const coverageStartPx = camX + minY * scale;
    const coverageEndPx = camX + maxY * scale;
    
    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('sideView')} (Y-Z)</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                <defs>
                    <marker id="arrowhead-td2000g3-side" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                    </marker>
                </defs>
                <line x1="0" y1={groundY} x2={width} y2={groundY} stroke="#9ca3af" strokeWidth="1" />
                <text x={width - 5} y={groundY - 5} fontSize="10" fill="#6b7280" textAnchor="end">{t('ground')}</text>
                <line x1={camX} y1={camY} x2={camX} y2={groundY} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4" />
                <text x={camX + 5} y={camY} fontSize="10" fill="#6b7280">H: {formatLength(installationHeight, unitSystem, 1)}</text>
                <polygon points={`${camX},${camY} ${visionEndPx},${groundY} ${visionStartPx},${groundY}`} fill="rgba(0, 180, 255, 0.1)" />
                <line x1={camX} y1={camY} x2={visionEndPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <line x1={camX} y1={camY} x2={visionStartPx} y2={groundY} stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <rect x={coverageStartPx} y={groundY - 5} width={coverageEndPx - coverageStartPx} height="10" fill="rgba(22, 163, 74, 0.7)" />
                <g transform={`translate(${camX}, ${camY}) rotate(${yTilt})`}>
                    <rect x="-10" y="-6" width="20" height="12" rx="2" fill="#0891b2" />
                    <circle cx="0" cy="0" r="4" fill="white" />
                </g>
                <line x1={coverageStartPx} y1={groundY + 20} x2={coverageEndPx} y2={groundY + 20} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td2000g3-side)" markerEnd="url(#arrowhead-td2000g3-side)"/>
                <text x={coverageStartPx + (coverageEndPx - coverageStartPx)/2} y={groundY + 33} fontSize="10" fill="#3b82f6" textAnchor="middle">{t('depthLabel')}: {formatLength(maxY-minY, unitSystem, 1)}</text>
            </svg>
        </div>
    );
};

const TopDownDiagram = ({ results }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    if (!results || !results.summary) return null;

    const { coverageField, visionField, coverageAvgWidth, coverageAvgHeight, visionAvgWidth, visionAvgHeight } = results.summary;
    
    const width = 500, height = 400, padding = 40;
    
    const allPoints = [...visionField, {x:0, y:0}];
    const xCoords = allPoints.map(p => p.x);
    const yCoords = allPoints.map(p => p.y);

    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    const dataWidth = maxX - minX;
    const dataHeight = maxY - minY;

    const dataCenterX = (minX + maxX) / 2;
    const dataCenterY = (minY + maxY) / 2;

    const scale = Math.min(
        dataWidth > 0 ? (width - padding * 2) / dataWidth : Infinity,
        dataHeight > 0 ? (height - padding * 2) / dataHeight : Infinity
    );

    const toSvgCoord = (p) => ({
        x: (width / 2) + (p.x - dataCenterX) * scale,
        y: (height / 2) - (p.y - dataCenterY) * scale,
    });
    
    const visionPolyPoints = visionField.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
    const coveragePolyPoints = coverageField.map(toSvgCoord).map(p => `${p.x},${p.y}`).join(' ');
    
    const getBbox = (field) => {
        const xPts = field.map(p => p.x);
        const yPts = field.map(p => p.y);
        return {
            minX: Math.min(...xPts), maxX: Math.max(...xPts),
            minY: Math.min(...yPts), maxY: Math.max(...yPts),
        };
    };

    const visionBbox = getBbox(visionField);
    const coverageBbox = getBbox(coverageField);

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full">
            <h2 className="text-xl font-bold mb-4 text-gray-700">{t('topDownView')} (X-Y)</h2>
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto border rounded-md">
                 <defs>
                    <marker id="arrowhead-td2000g3-top" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                    </marker>
                </defs>
                <polygon points={visionPolyPoints} fill="rgba(0, 180, 255, 0.1)" stroke="rgba(0, 180, 255, 0.4)" strokeWidth="1" />
                <polygon points={coveragePolyPoints} fill="rgba(22, 163, 74, 0.2)" stroke="rgba(22, 163, 74, 0.8)" strokeWidth="1.5" />
                <line x1={toSvgCoord({x: visionBbox.minX, y: 0}).x} y1={toSvgCoord({x: 0, y: visionBbox.maxY}).y - 10} x2={toSvgCoord({x: visionBbox.maxX, y: 0}).x} y2={toSvgCoord({x: 0, y: visionBbox.maxY}).y - 10} stroke="rgba(0, 180, 255, 0.7)" strokeWidth="1" markerStart="url(#arrowhead-td2000g3-top)" markerEnd="url(#arrowhead-td2000g3-top)"/>
                <text x={toSvgCoord({x: dataCenterX, y: 0}).x} y={toSvgCoord({x: 0, y: visionBbox.maxY}).y - 15} fontSize="10" fill="rgba(0, 180, 255, 1)" textAnchor="middle">{t('widthLabel')}: {formatLength(visionAvgWidth, unitSystem, 1)}</text>
                <line x1={toSvgCoord({x: visionBbox.maxX, y: 0}).x + 10} y1={toSvgCoord({x: 0, y: visionBbox.minY}).y} x2={toSvgCoord({x: visionBbox.maxX, y: 0}).x + 10} y2={toSvgCoord({x: 0, y: visionBbox.maxY}).y} stroke="rgba(0, 180, 255, 0.7)" strokeWidth="1" markerStart="url(#arrowhead-td2000g3-top)" markerEnd="url(#arrowhead-td2000g3-top)"/>
                <text x={toSvgCoord({x: visionBbox.maxX, y: 0}).x + 15} y={toSvgCoord({x: 0, y: dataCenterY}).y} fontSize="10" fill="rgba(0, 180, 255, 1)" textAnchor="start" dominantBaseline="middle">{t('depthLabel')}: {formatLength(visionAvgHeight, unitSystem, 1)}</text>
                <line x1={toSvgCoord({x: coverageBbox.minX, y: 0}).x} y1={toSvgCoord({x: 0, y: coverageBbox.minY}).y + 10} x2={toSvgCoord({x: coverageBbox.maxX, y: 0}).x} y2={toSvgCoord({x: 0, y: coverageBbox.minY}).y + 10} stroke="#3b82f6" strokeWidth="1" markerStart="url(#arrowhead-td2000g3-top)" markerEnd="url(#arrowhead-td2000g3-top)"/>
                <text x={toSvgCoord({x: dataCenterX, y: 0}).x} y={toSvgCoord({x: 0, y: coverageBbox.minY}).y + 22} fontSize="10" fill="#3b82f6" textAnchor="middle">{t('widthLabel')}: {formatLength(coverageAvgWidth, unitSystem, 1)}</text>
                <line x1={toSvgCoord({x: coverageBbox.minX, y: 0}).x - 10} y1={toSvgCoord({x: 0, y: coverageBbox.minY}).y} x2={toSvgCoord({x: coverageBbox.minX, y: 0}).x - 10} y2={toSvgCoord({x: 0, y: coverageBbox.maxY}).y} stroke="#ef4444" strokeWidth="1" markerStart="url(#arrowhead-td2000g3-top)" markerEnd="url(#arrowhead-td2000g3-top)"/>
                <text x={toSvgCoord({x: coverageBbox.minX, y: 0}).x - 15} y={toSvgCoord({x: 0, y: dataCenterY}).y} fontSize="10" fill="#ef4444" textAnchor="end" dominantBaseline="middle">{t('depthLabel')}: {formatLength(coverageAvgHeight, unitSystem, 1)}</text>
                <circle cx={toSvgCoord({x:0,y:0}).x} cy={toSvgCoord({x:0,y:0}).y} r="5" fill="#0891b2" />
                <text x={toSvgCoord({x:0,y:0}).x} y={toSvgCoord({x:0,y:0}).y + 15} fontSize="10" textAnchor="middle">{t('cameraPosition')}</text>
                <text x={10} y={20} fontSize="12" fill="rgba(0, 180, 255, 1)">{t('viewingRange')}</text>
                <text x={10} y={35} fontSize="12" fill="rgba(22, 163, 74, 1)">{t('countingRange')}</text>
            </svg>
        </div>
    );
};

const ParametersAndDerivedPanel = ({ params, setParams, onReset, isAdmin, intermediate }) => {
    const { t } = useTranslation();
    const { unitSystem } = useUnit();
    const handleParamChange = (key, value) => {
        setParams(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
    };
    
    const handleMeterParamChange = (key, displayValue) => {
        setParams(prev => ({ ...prev, [key]: getMetersFromInput(parseFloat(displayValue) || 0, unitSystem) }));
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-700">{t('modelParamsTitle')}</h2>
                {isAdmin && (
                    <button onClick={onReset} className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white text-sm font-semibold rounded-md hover:bg-gray-600 transition-colors">
                        <RefreshCw className="w-4 h-4"/> {t('reset')}
                    </button>
                )}
            </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <ParameterInput label={t('personHeight')} value={getDisplayValue(params.personHeight, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('personHeight', e.target.value)} isEditable={isAdmin} />
                    <ParameterInput label={t('shoulderTargetPixel')} value={params.shoulderTargetPixel} unit="px" onChange={(e) => handleParamChange('shoulderTargetPixel', e.target.value)} isEditable={isAdmin} />
                    <ParameterInput label={t('detectionResWidth')} value={params.detectionResolutionWidth} unit="px" onChange={(e) => handleParamChange('detectionResolutionWidth', e.target.value)} isEditable={isAdmin} />
                    <ParameterInput label={t('magnificationRefHeight')} value={getDisplayValue(params.magnificationPersonHeightRef, unitSystem).toFixed(2)} unit={getUnitLabel(unitSystem)} onChange={(e) => handleMeterParamChange('magnificationPersonHeightRef', e.target.value)} isEditable={isAdmin} tooltip="Reference height for calculating magnification factor"/>
                    <ParameterInput label={t('waterDropEffectBase')} value={params.waterDropBaseHeight} unit="" onChange={(e) => handleParamChange('waterDropEffectBase', e.target.value)} isEditable={isAdmin} tooltip="Parameter affecting the vertical FoV 'water-drop' effect"/>
                 </div>
                 {intermediate && (
                     <div className="mt-6 pt-4 border-t">
                         <h3 className="text-lg font-bold text-gray-700 mb-2">{t('intermediateValues')}</h3>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                             <ParameterInput label={t('magnificationFactor')} value={intermediate.autoMagnificationFactor.toFixed(3)} unit="" isEditable={false}/>
                             <ParameterInput label={t('lensToHeadVertical')} value={getDisplayValue(intermediate.shoulderDistance, unitSystem).toFixed(3)} unit={getUnitLabel(unitSystem)} isEditable={false}/>
                             <ParameterInput label={t('rawHalfWidth1m')} value={getDisplayValue(intermediate.rawHalfWidth, unitSystem).toFixed(3)} unit={getUnitLabel(unitSystem)} isEditable={false} tooltip="Original physical half-width of the lens FoV at 1m"/>
                             <ParameterInput label={t('waterDropHalfHeight1m')} value={getDisplayValue(intermediate.halfHeight1m, unitSystem).toFixed(3)} unit={getUnitLabel(unitSystem)} isEditable={false} tooltip="Dynamic half-height of the FoV at 1m, affected by the 'water-drop' effect"/>
                         </div>
                     </div>
                 )}
        </div>
    );
};

export default function Td2000g3Bg3Calculator({ isAdmin }) {
    const { t } = useTranslation();
    const [inputs, setInputs] = useState({
        installationHeight: 4.2,
        xTilt: 0,
        yTilt: 0,
        lensType: "2mm",
    });

    useEffect(() => {
        if (!isAdmin) {
            setInputs(prevInputs => ({
                ...prevInputs,
                xTilt: Math.max(-10, Math.min(10, prevInputs.xTilt)),
                yTilt: Math.max(-10, Math.min(10, prevInputs.yTilt)),
            }));
        }
    }, [isAdmin]);

    const getMergedParams = () => {
        const defaults = { ...defaultConfig };
        try {
            const stored = localStorage.getItem(PARAMS_STORAGE_KEY);
            return stored ? { ...defaults, ...JSON.parse(stored) } : defaults;
        } catch (e) {
            console.error("Failed to parse params from localStorage", e);
            return defaults;
        }
    };

    const [params, setParams] = useState(getMergedParams);
    
    useEffect(() => {
        if (isAdmin) {
            localStorage.setItem(PARAMS_STORAGE_KEY, JSON.stringify(params));
        }
    }, [params, isAdmin]);

    const handleResetParams = () => {
        localStorage.removeItem(PARAMS_STORAGE_KEY);
        setParams({ ...defaultConfig });
    };

    const calculationResults = useMemo(() => {
        return calculateTd2000g3Bg3Coverage(inputs, params, t);
    }, [inputs, params, t]);

    if (calculationResults.error) {
        return (
            <div className="p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-lg">
                <p className="font-bold">{t('calculationError')}</p>
                <p>{calculationResults.error}</p>
            </div>
        );
    }
    
    if (isAdmin) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-8">
                    <Controls
                        inputs={inputs}
                        setInputs={setInputs}
                        isAdmin={isAdmin}
                    />
                     <ParametersAndDerivedPanel
                        params={params}
                        setParams={setParams}
                        onReset={handleResetParams}
                        isAdmin={isAdmin}
                        intermediate={calculationResults.intermediate}
                    />
                </div>
                <div className="lg:col-span-2 space-y-8">
                    <SummaryResults data={calculationResults} />
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                        <SideViewDiagram inputs={inputs} results={calculationResults} />
                        <TopDownDiagram results={calculationResults} />
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className="flex flex-col gap-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1">
                        <Controls
                            inputs={inputs}
                            setInputs={setInputs}
                            isAdmin={isAdmin}
                        />
                    </div>
                    <div className="lg:col-span-2">
                        <SummaryResults data={calculationResults} />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <SideViewDiagram inputs={inputs} results={calculationResults} />
                    <TopDownDiagram results={calculationResults} />
                </div>
            </div>
        );
    }
}
// 功能描述: TD2000G3/BG3型号客流摄像机的覆盖范围计算器，并导出其核心计算逻辑。
